import { Theme } from '@material-ui/core';
import { ThemeProvider } from '@material-ui/core/styles';
import React, { createContext, ReactNode, useContext, useMemo } from 'react';
import { getActiveSiteDefinition } from 'src/config/sites';
import { getSiteRuntimeConfig, SiteRuntimeConfig } from 'src/config/sites/runtimeShellConfig';
import { SiteDefinition } from 'src/config/sites/SiteDefinition';
import { getAppThemeById } from 'src/Theme';

interface SiteProviderProps {
    children: ReactNode;
    siteId?: string;
}

interface SiteContextValue {
    site: SiteDefinition;
    runtimeConfig: SiteRuntimeConfig;
}

const defaultSite = getActiveSiteDefinition();
const defaultSiteRuntimeConfig = getSiteRuntimeConfig(
    defaultSite.id,
    defaultSite.routes.map(route => route.key)
);
const SiteContext = createContext<SiteContextValue>({ site: defaultSite, runtimeConfig: defaultSiteRuntimeConfig });

export const useSiteDefinition = (): SiteDefinition => useContext(SiteContext).site;

export const useEnabledSiteModuleKeys = (): Set<string> => {
    const { runtimeConfig } = useContext(SiteContext);
    return useMemo(() => new Set(runtimeConfig.modules), [runtimeConfig]);
};

const SiteProvider: React.FC<SiteProviderProps> = ({ children, siteId }) => {
    const detectedHostname = typeof window !== 'undefined' ? window.location.hostname : undefined;

    const site = useMemo(() => getActiveSiteDefinition(detectedHostname, siteId), [detectedHostname, siteId]);
    const runtimeSiteConfig = useMemo(
        () =>
            getSiteRuntimeConfig(
                site.id,
                site.routes.map(route => route.key)
            ),
        [site]
    );

    const shouldApplySiteTheme = runtimeSiteConfig.theme === 'site';
    const appTheme = useMemo(() => getAppThemeById(runtimeSiteConfig.appThemeId), [runtimeSiteConfig.appThemeId]);
    const siteContextValue = useMemo(() => ({ site, runtimeConfig: runtimeSiteConfig }), [runtimeSiteConfig, site]);

    const siteThemedChildren =
        site.theme && shouldApplySiteTheme ? (
            <ThemeProvider theme={(outerTheme: Theme) => (site.theme ? site.theme(outerTheme) : outerTheme)}>
                {children}
            </ThemeProvider>
        ) : (
            children
        );

    const themedChildren = <ThemeProvider theme={appTheme}>{siteThemedChildren}</ThemeProvider>;

    return <SiteContext.Provider value={siteContextValue}>{themedChildren}</SiteContext.Provider>;
};

export default SiteProvider;
